import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './BulkUploadMain.scss';
import { CoreModal, CoreTextfield, CoreUploader } from '@komodo-design-system/komodo-components-react';
import { UploadedFile } from '@komodo-design-system/komodo-core-web-components';
import ActionButton from 'componentsV2/AssestUpload/BulkUpload/ActionButton';
import Loading from 'components/Placeholders/Loading';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import FileUploadService from './fileUploadService';
import ProgressBar from './ProgressBar';
import { JOBS } from 'constants/routes';
import { useNavigate } from 'react-router';
import { 
  BulkUploadUtils, 
  AlertState, 
  UploadProgress 
} from './BulkUploadMainUtils';
import ConfirmationAlertBox from 'componentsV2/SharedAlertBox/ConfirmationAlertBox/ConfirmationAlertBox';
import BulkUploadGuidelines from './BulkUploadGuidelines';
import { FileUploadUtils } from './fileUploadUtils';

interface BulkUploadMainProps {
  defaults?: any;
  assetType: string;
  setAssetType: any
}

const BulkUploadMain: React.FC<BulkUploadMainProps> = ({ assetType, defaults, setAssetType }) => {
  const [filesList, setFilesList] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bulkJobId, setBulkJobId] = useState('');
  const [jobName, setJobName] = useState('');
  const bulkUploadRef = React.useRef<HTMLCoreUploaderElement>(null);
  const [alert, setAlert] = useState<AlertState>(
    BulkUploadUtils.createAlert('', false)
  );
  const [confirmationAlert, setConfirmationAlert] = useState({open: false, reason: '', message:''});
  
  // Memoized selector to prevent unnecessary re-renders
  const userTypeData = useSelector((state: any) => state?.userTypeReducer?.userType?.data);
  const userName = useMemo(() => userTypeData?.userName || '', [userTypeData?.userName]);
  
  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    show: false,
    completed: 0,
    total: 0,
    currentFile: '',
    stage: ''
  });

  useEffect(() => {
    setBulkJobId('');
    setJobName('');
    setFilesList([]);
  }, [assetType]);
  // Handle: File count exceeded - clean up and show error
  const handleFileCountExceeded = (newFiles: UploadedFile[]): void => {
    if (filesList.length === 0) {
      setFilesList([]);
    } else {
      const existingFiles = BulkUploadUtils.filterExistingFiles(newFiles, filesList);
      setFilesList([...existingFiles]);
    }
    
    setAlert(BulkUploadUtils.createAlert(BulkUploadUtils.ERROR_MESSAGES.FILE_COUNT_EXCEEDED));
  };

  // Handle: Oversized files - set valid files and show error
  const handleOversizedFiles = (validFiles: UploadedFile[]): void => {
    setAlert(BulkUploadUtils.createAlert(BulkUploadUtils.ERROR_MESSAGES.FILE_SIZE_EXCEEDED));
    setFilesList([...validFiles]);
  };

  // Main upload file handler - clean and modular
  const uploadFile = (event: CustomEvent<UploadedFile[]>) => {
    if (event.detail.length === 0) {
      return;
    }

    // Step 1: Check file count limit
    if (!BulkUploadUtils.validateFileCount(event.detail)) {
      handleFileCountExceeded(event.detail);
      return;
    }

    // Step 2: Process files for size validation
    const validation = BulkUploadUtils.processValidFiles(event.detail);

    // Step 3: Update state based on validation results
    if (!validation.isValid) {
      handleOversizedFiles(validation.validFiles);
    } else {
      bulkUploadRef.current?.scrollIntoView({ behavior: 'smooth' });
      setFilesList([...event.detail]);
    }
  };

  // Submit handler: Orchestrates all validation and upload steps
  const handleSubmitFile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = BulkUploadUtils.validateFileTypes(filesList, defaults.is_metadata_only);
    if (!validation.isValid) {
      setAlert(BulkUploadUtils.createAlert(validation.errorMessage));
      return;
    }
    
    await processFileUpload();
  };

  const getJobId = async (status: string, type: string, errorMessage: any, fileName: any): Promise<string | null> => {
    const jobId = await BulkUploadUtils.createJobId(
      status, 
      type, 
      errorMessage, 
      userName, 
      jobName, 
      bulkJobId,
      fileName
    );
    
    setIsLoading(false);
    
    if (jobId) {
      setBulkJobId(jobId);
    }
    
    return jobId;
  };

  // Upload: Process the actual file upload with FileUploadService
  const processFileUpload = async () => {
    setIsLoading(true);
    
    // Find out the excel file name from filesList (moved outside try block)
    const excelFile = filesList.find(file => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    });
    const excelFileName = excelFile?.name || filesList[0]?.name || 'unknown_file';
    
    try {
      // Create progress update handler using utility
      const handleProgressUpdate = BulkUploadUtils.createProgressHandler(setUploadProgress);
      
      // Create upload service instance with progress callback
      const uploadService = new FileUploadService(handleProgressUpdate);
      // Get the jobId before uploading
      const jobId = await getJobId('CREATED', 'POST', '', excelFileName);
      if (!jobId) {
        setIsLoading(false);
        setAlert(BulkUploadUtils.createAlert(BulkUploadUtils.ERROR_MESSAGES.JOB_ID_CREATION_FAILED));
        return;
      }
      const totalFiles = await FileUploadUtils.calculateTotalFiles(filesList);
      setUploadProgress({
        show: true,
        completed: 0,
        total: totalFiles,
        currentFile: '',
        stage: 'Starting upload...'
      });
      
      // Process the upload
      const result = await uploadService.processFileUpload(filesList, jobId, totalFiles);
      if (result.success) {
        // Handle successful upload
        setAlert(BulkUploadUtils.createAlert(
          `Bulk upload completed successfully! ${result.uploadedCount} file(s) processed.`,
          true,
          true
        ));
        // Clear form after successful upload
        setFilesList([]);
        setJobName('');
      } else {
        // Handle upload errors
        await getJobId('FAILED', 'PUT', result.message, excelFileName);
        setAlert(BulkUploadUtils.createAlert(
          result.message || BulkUploadUtils.ERROR_MESSAGES.UPLOAD_FAILED
        ));
      }
      
    } catch (error) {
      await getJobId('FAILED', 'PUT', error, excelFileName);
      setAlert(BulkUploadUtils.createAlert(BulkUploadUtils.ERROR_MESSAGES.UPLOAD_FAILED));
    } finally {
      setIsLoading(false);
    }
  };

  const clearUploadData = () => {
    setConfirmationAlert({
      open: true,
      reason: 'clearData',
      message: BulkUploadUtils.ERROR_MESSAGES.CLEAR_ALL
    });
  };

  const handleCloseFile = (id: any) => {
    // Use functional state update to ensure we have the latest state
    setFilesList(prevFiles => {
      const updatedFilesList = prevFiles.filter(file => file.id !== id);
      // Force the uploader component to re-render by updating its files prop
      if (bulkUploadRef.current) {
        // Trigger a small delay to ensure the component updates properly
        setTimeout(() => {
          if (bulkUploadRef.current) {
            bulkUploadRef.current.files = updatedFilesList;
          }
        }, 0);
      }
      return updatedFilesList;
    });
  };

  const handleRejectCallback = (file: File) => {
    setAlert(BulkUploadUtils.createAlert(
      BulkUploadUtils.ERROR_MESSAGES.FILE_REJECTED(file.name)
    ));
  };

  const handleAlertClose = () => {
    setAlert(prev => ({ ...prev, open: false }));
    if (alert.props?.variant) {
      navigate(JOBS);
    }
  };

  const handleActions = (action: string) => {
    if( action === 'YES'){
      setBulkJobId('');
      setFilesList([]);
      setJobName('');
      setAssetType({ value: '', label: '' });
      setConfirmationAlert({open: false, message: '', reason:''});
    }
    else{
      setConfirmationAlert({open: false, message: '', reason:''});
    }
  };
  return (
    <div className="bulk-upload-main">
      {isLoading && <Loading/>}
      <div className="upload-section">
        <div className="uploader-container">
          <CoreUploader
            key={`uploader-${filesList.length}-${filesList.map(f => f.id).join('-')}`}
            ref={bulkUploadRef}
            className={filesList.length > 0 ? 'uploader' : ''}
            files={filesList}
            showicon
            variant="full-size"
            onFileListUpdated={uploadFile}
            acceptedformats='.xlsx, .xls, .zip'
            showcontrols={true}
            filerejectcallback={handleRejectCallback}
            closeFileCallback={handleCloseFile}
          >
          </CoreUploader>
        </div>
      </div>

      <div className="job-name">
        <label className="required-field">
          Job Name
        </label>
        <CoreTextfield
          type="text"
          required
          value={jobName}
          placeholder="Job name"
          onTextfieldChange={(e) =>
            setJobName(e.target.value.trim())
          }></CoreTextfield>
      </div>
      <BulkUploadGuidelines />
      {/* Progress Bar */}
      {uploadProgress.show && (
        <div className="upload-progress">
          <CoreModal 
            data-testid="upload-progress" 
            bgclose={false} 
            isopen={uploadProgress.show} 
            header="Upload is in progress. Please wait!" 
            closeicon={false}>
            <ProgressBar 
              progress={BulkUploadUtils.calculateProgress(uploadProgress.completed, uploadProgress.total)} 
              bgcolor="#99ccff" 
            />
            {uploadProgress.currentFile && <h5>{uploadProgress.currentFile}</h5>}
            {uploadProgress.stage && <h6>{uploadProgress.stage}</h6>}
          </CoreModal>
        </div>
      )}
      <ActionButton 
        isDisabled={isLoading || uploadProgress.show || filesList.length === 0 || !jobName?.trim() || !assetType} 
        submitFile={handleSubmitFile}
        clearUploadData={clearUploadData}
        isResetDisabled={filesList.length === 0 && jobName.length ===0 && !assetType}
      />
      <ConfirmationAlertBox
        cancelText="Cancel"
        isDisabled={false}
        okText="Yes"
        isModalOpen={confirmationAlert.open}
        inputProps={{ reason: confirmationAlert.reason }}
        handleAction={handleActions}
        message={confirmationAlert.message}
      />
      <AlertBox
        message={alert.message}
        isModalOpen={alert.open}
        handleClose={handleAlertClose}
      />
    </div>
  );
};

export default BulkUploadMain;
